// BL598 Phase 2 — `grid describe entity <slug> [--builds]`.
//
// Shows an entity's status, URL, owner, and last deploy. `--builds` appends the
// recent-deploy history (the surface previously served by the deleted
// `grid builds` verb). Reads GET /api/v2/runtimes/:id and, under
// `--builds`, GET /api/v2/entities/:id/deploys.
//
// Entity resolution mirrors `info`: no arg → the linked entity; a UUID or
// `grid/slug` ref → addressed directly; a bare slug → resolved uniquely across
// every grid you belong to.

import { Command } from 'commander';
import type { IV2Client } from '../../api/v2-client.js';
import { getV2Client } from '../../api/v2-client.js';
import { requireLink } from '../../services/link.js';
import { isGlobalEntityRef } from '../../services/entity-ref.js';
import {
  resolveBareSlugAcrossOrgs,
  bareSlugNotFoundMessage,
} from '../../services/resolve-bare-slug.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { addOutputOption, isStructuredOutput } from '../../services/output-format.js';
import { CloudGridError } from '../../utils.js';
import type { V2RuntimeDetailResponse } from '../status.js';
import { formatRelativeTime } from '../status.js';

// ── Deploy shape (mirrors routes/v2/entities.ts GET /:id/deploys) ───────────

interface DeploySummary {
  trace_id: string | null;
  type: 'deploy' | 'teardown';
  status: 'building' | 'deploying' | 'success' | 'failed';
  source: string;
  triggered_by: string;
  started_at: string;
  completed_at: string | null;
  duration_seconds: number | null;
  failed_step: string | null;
  build_ids: string[];
}

interface DeploysResponse {
  entity: { id: string; name: string };
  deploys: DeploySummary[];
}

export interface DescribeEntityOptions {
  builds?: boolean;
  limit?: string;
  json?: boolean;
  output?: string;
}

const DEFAULT_LIMIT = 10;
const MAX_LIMIT = 50;

function parseLimit(raw: string | undefined): number {
  if (raw === undefined) return DEFAULT_LIMIT;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > MAX_LIMIT) {
    throw new CloudGridError(`--limit must be an integer between 1 and ${MAX_LIMIT}.`);
  }
  return n;
}

async function resolveEntityId(
  nameArg: string | undefined,
  client: IV2Client,
): Promise<string> {
  if (nameArg === undefined) return requireLink().entity_id;
  const arg = nameArg.trim();
  if (isGlobalEntityRef(arg)) return arg;
  const match = await resolveBareSlugAcrossOrgs(arg, client);
  if (!match) {
    throw new CloudGridError(bareSlugNotFoundMessage(arg, 'describe entity'));
  }
  return match.entity_id;
}

// ── Rendering ───────────────────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDuration(secs: number | null): string {
  if (secs === null) return '—';
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function renderDetail(entity: V2RuntimeDetailResponse): string {
  const label = entity.display_name || entity.name;
  const lines: string[] = [];
  lines.push(`${label}  (${entity.kind})`);
  lines.push(`  Grid:        ${entity.org.slug}`);
  lines.push(`  Space:       ${entity.space != null ? String(entity.space) : '—'}`);
  lines.push(`  Owner:       ${entity.owner ? entity.owner.email : '—'}`);
  lines.push(`  Status:      ${entity.status ?? '—'}`);
  lines.push(`  URL:         ${entity.url || '—'}`);
  if (entity.last_deploy !== null) {
    lines.push(
      `  Last deploy: ${entity.last_deploy.status}  ${formatRelativeTime(entity.last_deploy.deployed_at)}`,
    );
  } else {
    lines.push(`  Last deploy: —`);
  }
  return lines.join('\n');
}

function renderDeploys(resp: DeploysResponse): string {
  const lines: string[] = [];
  if (resp.deploys.length === 0) {
    return 'Deploys: none yet';
  }
  lines.push(`Deploys (${resp.deploys.length}):`);
  lines.push(
    `  ${'TRIGGERED'.padEnd(18)} ${'STATUS'.padEnd(12)} ${'FAILED STEP'.padEnd(16)} BUILD`,
  );
  for (const d of resp.deploys) {
    const triggered = formatTimestamp(d.started_at).padEnd(18);
    const dur =
      d.status === 'success' || d.status === 'failed'
        ? ` (${formatDuration(d.duration_seconds)})`
        : '';
    const status = `${d.status}`.padEnd(12);
    const step = (d.failed_step ?? '—').padEnd(16);
    lines.push(`  ${triggered} ${status} ${step} ${d.trace_id ?? '—'}${dur}`);
  }
  return lines.join('\n');
}

// ── Core runner ─────────────────────────────────────────────────────────────

export async function runDescribeEntity(
  nameArg: string | undefined,
  opts: DescribeEntityOptions,
  client?: IV2Client,
): Promise<void> {
  const apiClient = client ?? (await getV2Client());
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const resolvedId = await resolveEntityId(nameArg, apiClient);

  const entity = await apiClient.get<V2RuntimeDetailResponse>(
    `/api/v2/runtimes/${encodeURIComponent(resolvedId)}`,
  );

  let deploys: DeploysResponse | null = null;
  if (opts.builds) {
    const limit = parseLimit(opts.limit);
    deploys = await apiClient.get<DeploysResponse>(
      `/api/v2/entities/${encodeURIComponent(entity.entity_id ?? resolvedId)}/deploys`,
      { limit },
    );
  }

  out.json(() => ({ entity, deploys: deploys?.deploys ?? null }));
  out.human(() => {
    const blocks = [renderDetail(entity)];
    if (deploys) blocks.push(renderDeploys(deploys));
    return blocks.join('\n\n');
  });
}

export const describeEntityCommand = addOutputOption(
  new Command('entity')
    .description('Show an entity: status, URL, recent deploys')
    .argument('[slug]', 'Entity slug or UUID (uses the linked entity if omitted)')
    .option('--builds', 'Include recent deploy history')
    .option('--limit <n>', `Deploys to show with --builds (1-${MAX_LIMIT}, default ${DEFAULT_LIMIT})`)
    .option('--json', 'machine-readable output (alias of -o json)'),
).action(async (slug: string | undefined, opts: DescribeEntityOptions) => {
  try {
    await runDescribeEntity(slug, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
